import { useState, useRef, useEffect } from "react";
import { askFastGPT } from "@/services/fastgpt";

interface Message {
  role: "user" | "ai";
  text: string;
  time: string;
}

const suggestions = [
  "查询商茂世纪广场",
  "公教一村在哪里",
];

const initialMessages: Message[] = [
  {
    role: "ai",
    text: "您好！我是空天数据AI助手，可以帮您快速查询地理数据、分析区域信息，或指导您使用平台功能。请问有什么可以帮助您？",
    time: "10:00",
  },
];

function getTime() {
  const d = new Date();
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

export default function AIAgent({ onAiSearchQuery }: { onAiSearchQuery?: (keyword: string) => void } = {}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || typing) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg, time: getTime() }]);
    setTyping(true);
    try {
      const data = await askFastGPT(msg);
      // reply 展示在对话框
      setMessages((prev) => [...prev, { role: "ai", text: data.reply || "暂未获取到回答", time: getTime() }]);
      // keyword 触发搜索
      const keyword = data.extracted?.keyword;
      if (keyword && onAiSearchQuery) {
        onAiSearchQuery(keyword);
      }
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: `请求失败：${err.message}`, time: getTime() },
      ]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <div className="fixed bottom-20 left-4 z-[1000]">
      {open && (
        <div
          className="fixed bottom-[84px] left-[70px] w-80 bg-white/95 backdrop-blur-md border border-slate-200 rounded-2xl shadow-xl overflow-hidden flex flex-col z-50"
          style={{ height: "420px", border: "none" }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-2 px-4 py-3 border-b border-slate-100"
            style={{ background: "linear-gradient(135deg, #1e3d7a, #2550a0)" }}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-robot-2-line text-white text-sm"></i>
            </div>
            <div>
              <div className="text-white text-sm font-semibold">AI智能助手</div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                <span className="text-emerald-300 text-xs">在线</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto text-blue-100 hover:text-white cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50/50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "ai" && (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                    <i className="ri-robot-2-line text-white text-xs"></i>
                  </div>
                )}
                <div className={`max-w-[80%] ${msg.role === "user" ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                  <div
                    className={`px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                      msg.role === "user"
                        ? "bg-blue-500 text-white rounded-br-sm"
                        : "bg-white text-slate-700 rounded-bl-sm border border-slate-200"
                    }`}
                  >
                    {msg.text}
                  </div>
                  <span className="text-slate-400 text-xs">{msg.time}</span>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-2-line text-white text-xs"></i>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 flex gap-1">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            )}
            <div ref={bottomRef}></div>
          </div>

          {/* Suggestions */}
          <div className="px-3 pb-2 pt-2 flex flex-wrap gap-1 border-t border-slate-100">
            {suggestions.slice(0, 2).map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs bg-blue-50 border border-blue-200 text-blue-600 hover:text-blue-800 hover:border-blue-400 rounded-full px-2 py-0.5 transition-colors cursor-pointer whitespace-nowrap"
              >
                {s}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-3 pb-3 pt-2">
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="输入问题..."
                disabled={typing}
                className="flex-1 bg-transparent text-slate-800 placeholder-slate-400 text-xs px-3 py-2 outline-none disabled:opacity-50"
              />
              <button
                onClick={() => send()}
                disabled={typing}
                className="px-3 py-2 text-blue-500 hover:text-blue-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                <i className="ri-send-plane-fill text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bubble trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-110 cursor-pointer relative z-50"
        style={{ background: "linear-gradient(135deg, #2550a0, #0891b2)" }}
      >
        <div className="absolute inset-0 rounded-full animate-ping opacity-15 bg-blue-400"></div>
        {open ? (
          <i className="ri-close-line text-white text-2xl relative z-10"></i>
        ) : (
          <i className="ri-robot-2-line text-white text-2xl relative z-10"></i>
        )}
      </button>
    </div>
  );
}
